import { useState } from 'react'
import { ConnectableAccount, IntegrationAuth } from 'api/clients/IntegrationAuthsClient'
import { showToastMessage, showIntegrationSuccessToast } from 'common/ToastMessage/ShowToastMessage'
import { showIntegrationErrorToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { useModals } from 'almanac-ui/Modal'
import { useRootStore } from 'stores/RootStore'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useMutation, useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateExternalIntegration } from 'screens/OAuthRedirectV2/hooks'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { createIntegrationAuths } from 'components/Onboarding/CreateBrandStyleModal/CreateBrandStyleModal'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { SelectConnectableAccountsDrawer } from 'components/SelectConnectableAccountsDrawer/SelectConnectableAccountsDrawer'
import { SelectConnectableAccountsModal } from 'components/SelectConnectableAccountsModal/SelectConnectableAccountsModal'
import { NoAccountsFoundDrawer } from 'screens/OAuthRedirectV2/NoAccountsFound/NoAccountsFoundDrawer'
import { NoAccountsFoundModal } from 'screens/OAuthRedirectV2/NoAccountsFound/NoAccountsFoundModal'
import { fetchConnectableAccounts } from 'hooks/queries/workspaces/integrationAuths/useConnectableAccounts'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useOauthRefresh } from 'hooks/useOauthRefresh'
import { MetaAdsAccountSetupModal } from 'components/MetaAdsAccountSetupModal/MetaAdsAccountSetupModal'
import { useShowIntegrationSuccessModal } from 'screens/OAuthRedirectV2/hooks'

interface Props {
  provider?: ExternalIntegrationEnum
  isSingleSelect?: boolean
  allowReconnect?: boolean
}

export interface ConnectProps {
  onConnect?: (externalIntegrations: ExternalIntegration[]) => void
  onIntegrationAuthCreated?: (integrationAuth: IntegrationAuth) => void
  switchFromIntegrationId?: number
}

export const useConnectAccountPopup = (props?: Props) => {
  const { openModal, closeStack, closeModal } = useModals()
  const { drawersStore } = useRootStore()
  const { openDrawer, closeDrawer } = drawersStore
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const currentWorkspace = useCurrentWorkspace()
  const { handleRefresh } = useOauthRefresh()
  const { data: externalIntegrationsData } = useExternalIntegrations({
    select: (data) => {
      return {
        external_integrations: data.external_integrations.filter((externalIntegration) =>
          [
            ExternalIntegrationEnum.Facebook,
            ExternalIntegrationEnum.Instagram,
            ExternalIntegrationEnum.LinkedIn,
            ExternalIntegrationEnum.Google,
            ExternalIntegrationEnum.X,
            ExternalIntegrationEnum.Mailchimp,
            ExternalIntegrationEnum.GoHighLevel,
            ExternalIntegrationEnum.TikTok,
            ExternalIntegrationEnum.YouTube,
            ExternalIntegrationEnum.Shopify,
          ].includes(externalIntegration.provider),
        ),
      }
    },
  })
  const { mutateAsync: createExternalIntegration } = useCreateExternalIntegration()
  const { isMobile } = useResponsiveDesign()
  const { mutateAsync: createIntegrationAuth } = useMutation(createIntegrationAuths)
  const [isConnecting, setIsConnecting] = useState(false)
  const showIntegrationSuccessModal = useShowIntegrationSuccessModal()

  const handleConnectAccount =
    (
      integrationAuthId: number,
      provider: ExternalIntegrationEnum,
      onConnect?: (externalIntegrations: ExternalIntegration[]) => void,
      switchFromIntegrationId?: number,
    ) =>
    async (accounts: ConnectableAccount[]) => {
      const dismiss = showToastMessage('Connecting...', 'loading')
      const alreadyConnected = externalIntegrationsData?.external_integrations || []
      const newAccounts = accounts.filter(
        (account) => !alreadyConnected.some((integration) => integration.provider_id === account.provider_id),
      )

      const results = await Promise.all(
        newAccounts.map((account) =>
          createExternalIntegration({
            workspaceId,
            params: {
              ...account,
              integration_auth_id: integrationAuthId,
              ...(switchFromIntegrationId && { switch_from_integration_id: switchFromIntegrationId }),
            },
          }),
        ),
      )

      dismiss()

      const successfulResults = results.filter((res) => res.type === SUCCESS_RESPONSE_TYPE)
      const failedResults = results.filter((res) => res.type !== SUCCESS_RESPONSE_TYPE)

      // Show error for any failed results
      if (failedResults.length > 0) {
        showIntegrationErrorToast('Failed to connect account')
        setIsConnecting(false)
        return
      }

      // All succeeded
      const selectedIntegrations = successfulResults.map((res) => res.data.external_integration)

      // Invalidate the external integrations query to update the cache
      queryClient.invalidateQueries(['externalIntegrations', workspaceId])

      if (selectedIntegrations.length > 0) {
        showIntegrationSuccessToast(`Successfully connected to ${selectedIntegrations[0]?.name}!`)
      }

      isMobile ? closeDrawer() : closeModal()

      const onClose = () => {
        closeModal()
        onConnect?.(selectedIntegrations)
        setIsConnecting(false)
      }

      const modalShown = showIntegrationSuccessModal({
        provider,
        result: results[0],
        onClose,
      })

      if (!modalShown) {
        onConnect?.(selectedIntegrations)
        setIsConnecting(false)
      }
    }

  const connect = (
    popup: Window | undefined,
    options?: ConnectProps & { createdIntegration?: ExternalIntegration },
  ) => {
    setIsConnecting(true)

    // Handle non-OAuth providers like WordPress that don't have a popup window
    // The integration is already created, so we just need to get it and call onConnect
    if (!popup) {
      // If the created integration was passed in options, use it directly
      if (options?.createdIntegration) {
        options?.onConnect?.([options.createdIntegration])
        setIsConnecting(false)
        return
      }
      return
    }

    let safetyTimer: NodeJS.Timeout | null = null
    let visibilityHandler: (() => void) | null = null

    if (isMobile) {
      let hasLeftPage = false

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          hasLeftPage = true
        } else if (document.visibilityState === 'visible' && hasLeftPage) {
          setTimeout(() => {
            setIsConnecting(false)
          }, 2000)
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)
      visibilityHandler = handleVisibilityChange

      safetyTimer = setTimeout(() => {
        setIsConnecting(false)
      }, 30000)
    }

    const listener = async (event: MessageEvent) => {
      // eslint-disable-next-line no-restricted-properties
      if (window.location.origin !== event.origin) return

      if (event.data.params && event.data.provider) {
        const { params, provider, accountToRefreshId } = event.data

        if (event.data.params.error) {
          popup.close()
          showIntegrationErrorToast('There was an error connecting the account. Please try again.')
          return
        }

        if (accountToRefreshId && params && Object.keys(params).length > 0) {
          await handleRefresh(params, provider, accountToRefreshId, (integration) => {
            options?.onConnect?.([integration])
          })

          popup.close()
          setIsConnecting(false)
          return
        }

        try {
          const { data: integrationAuth } = await createIntegrationAuth({
            provider,
            params,
            workspaceId,
          })

          if (options?.onIntegrationAuthCreated) {
            popup.close()
            return options.onIntegrationAuthCreated(integrationAuth)
          }

          const connectableAccountsData = await queryClient.fetchQuery<{ data: ConnectableAccount[]; metadata: any }>(
            ['workspaces', workspaceId, 'integrationAuths', integrationAuth.id, 'connectableAccounts'],
            () => fetchConnectableAccounts(integrationAuth.id, workspaceId),
          )

          const connectableAccounts = connectableAccountsData.data
          const metadata = connectableAccountsData.metadata

          if (connectableAccounts.length === 0) {
            popup.close()
            isMobile
              ? openDrawer(NoAccountsFoundDrawer, { provider, onClose: closeDrawer })
              : openModal(NoAccountsFoundModal, { provider, onClose: closeModal })
            return
          }

          // Special handling for Meta Ads - close popup immediately after opening modal
          if (provider === ExternalIntegrationEnum.MetaAds) {
            popup.close()
            openModal(MetaAdsAccountSetupModal, {
              connectableAccounts,
              metadata,
              onCancel: () => {
                closeStack()
              },
              handleSubmit: handleConnectAccount(
                integrationAuth.id,
                provider,
                options?.onConnect,
                options?.switchFromIntegrationId,
              ),
            })
            return
          }

          // For other providers, close the popup immediately
          popup.close()

          const connectProps = {
            connectableAccounts,
            provider,
            onCancel: () => {},
            handleSubmit: handleConnectAccount(
              integrationAuth.id,
              provider,
              options?.onConnect,
              options?.switchFromIntegrationId,
            ),
            isSingleSelect: props?.isSingleSelect || currentWorkspace?.is_virtual_marketer,
            allowReconnect: props?.allowReconnect,
          }

          isMobile
            ? openDrawer(SelectConnectableAccountsDrawer, connectProps)
            : openModal(
                SelectConnectableAccountsModal,
                {
                  ...connectProps,
                  onCancel: () => {
                    closeStack()
                  },
                },
                { newStack: true },
              )
        } catch (e) {
          popup.close()
          showIntegrationErrorToast('There was an error connecting the account. Please try again.')
          reportError(e)
        } finally {
          safetyTimer && clearTimeout(safetyTimer)
          visibilityHandler && document.removeEventListener('visibilitychange', visibilityHandler)
          setIsConnecting(false)
        }
      }

      if (event.data.error) {
        popup.close()
        safetyTimer && clearTimeout(safetyTimer)
        visibilityHandler && document.removeEventListener('visibilitychange', visibilityHandler)
        showIntegrationErrorToast(event.data.error)
        setIsConnecting(false)
      }
    }

    const timer = setInterval(function () {
      if (popup.closed) {
        window.removeEventListener('message', listener)
        clearInterval(timer)
        safetyTimer && clearTimeout(safetyTimer)
        visibilityHandler && document.removeEventListener('visibilitychange', visibilityHandler)
        setIsConnecting(false)
      }
    }, 1000)
    window.addEventListener('message', listener)
  }

  return {
    connect,
    externalIntegration: props?.provider
      ? externalIntegrationsData?.external_integrations?.find(
          (externalIntegration) => externalIntegration.provider === props?.provider,
        )
      : externalIntegrationsData?.external_integrations?.[0],
    isConnecting,
  }
}
